import { useState, useEffect } from 'react'
import {
  CheckSquare,
  Square,
  Plus,
  Trash2,
  AlertTriangle,
  Clock,
  Sparkles,
  CheckCircle2,
  Check,
  Loader2,
  Calendar,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { Button, Input, Badge } from '../atoms'
import { cn } from '../atoms'
import { useUpdateCaseTasks } from '../../hooks/useCases'
import { useAuth } from '../../store/auth.store'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'

export interface CaseTask {
  id: string
  description: string
  urgency: 'CRITICO' | 'ALTO' | 'MEDIO' | 'BAJO' | string
  assigneeRole?: string
  isCompleted: boolean
  completedAt?: string | null
  completedBy?: string | null
  createdAt?: string
}

interface CaseTasksSectionProps {
  caseId: string
  tasks?: CaseTask[] | any
  canEdit?: boolean
  onTasksUpdated?: (tasks: CaseTask[]) => void
}

const urgencyBadgeConfig: Record<string, { bg: string; text: string; border: string }> = {
  CRITICO: { bg: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  ALTO: { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
  MEDIO: { bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  BAJO: { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
}

export function CaseTasksSection({
  caseId,
  tasks: initialTasks = [],
  canEdit = true,
  onTasksUpdated,
}: CaseTasksSectionProps) {
  const { user } = useAuth()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()
  const updateTasksMutation = useUpdateCaseTasks()

  const [tasks, setTasks] = useState<CaseTask[]>(() => {
    return Array.isArray(initialTasks) ? initialTasks : []
  })
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return !initialTasks || initialTasks.length === 0
  })
  const [newDesc, setNewDesc] = useState('')
  const [newUrgency, setNewUrgency] = useState<'CRITICO' | 'ALTO' | 'MEDIO' | 'BAJO'>('MEDIO')
  const [newRole, setNewRole] = useState('Operador')
  const [isAdding, setIsAdding] = useState(false)

  useEffect(() => {
    if (Array.isArray(initialTasks)) {
      setTasks(initialTasks)
    }
  }, [initialTasks])

  const total = tasks.length
  const completed = tasks.filter(t => t.isCompleted).length
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0

  const saveTasks = async (updatedList: CaseTask[], actionMessage?: string) => {
    setTasks(updatedList)
    if (onTasksUpdated) onTasksUpdated(updatedList)
    try {
      await updateTasksMutation.mutateAsync({
        caseId,
        tasks: updatedList,
      })
      if (actionMessage) successToast(actionMessage)
    } catch (err: any) {
      errorToast(err?.message || 'Error guardando tareas')
    }
  }

  const handleToggleTask = (taskId: string) => {
    const updated = tasks.map(task => {
      if (task.id === taskId) {
        const nextState = !task.isCompleted
        return {
          ...task,
          isCompleted: nextState,
          completedAt: nextState ? new Date().toISOString() : null,
          completedBy: nextState ? `${user?.firstName || 'Usuario'} ${user?.lastName || ''}`.trim() : null,
        }
      }
      return task
    })
    saveTasks(updated, 'Estado de tarea actualizado')
  }

  const handleAddTask = (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    const trimmed = newDesc.trim()
    if (!trimmed) return

    const newTask: CaseTask = {
      id: `task-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      description: trimmed,
      urgency: newUrgency,
      assigneeRole: newRole.trim() || 'Operador',
      isCompleted: false,
      completedAt: null,
      completedBy: null,
      createdAt: new Date().toISOString(),
    }

    const updated = [...tasks, newTask]
    saveTasks(updated, 'Tarea agregada al caso')
    setNewDesc('')
    setIsAdding(false)
  }

  const handleDeleteTask = (taskId: string) => {
    const updated = tasks.filter(t => t.id !== taskId)
    saveTasks(updated, 'Tarea eliminada')
  }

  return (
    <div className="rounded-xl border border-slate-200/80 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden transition-all">
      {/* Header (Desplegable) */}
      <div
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="px-5 py-3 border-b border-slate-100 dark:border-gray-800 flex flex-wrap items-center justify-between gap-3 bg-slate-50/50 dark:bg-gray-800/40 cursor-pointer select-none hover:bg-slate-100/60 dark:hover:bg-gray-800/80 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-950/60 flex items-center justify-center text-primary-600 dark:text-primary-400 flex-shrink-0">
            <CheckSquare className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Tareas Operativas del Caso
              </h3>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-gray-700">
                {completed} de {total} ({percent}%)
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Acciones requeridas para resolver el evento {isCollapsed ? '• Haz clic para desplegar' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {canEdit && !isAdding && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setIsCollapsed(false)
                setIsAdding(true)
              }}
              className="text-xs h-7 gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              Nueva Tarea
            </Button>
          )}

          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 rounded-lg hover:bg-slate-200 dark:hover:bg-gray-700 text-slate-500 dark:text-slate-400 transition-colors"
            title={isCollapsed ? 'Desplegar tareas' : 'Plegar tareas'}
          >
            {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-100 dark:bg-gray-800 h-1">
        <div
          className={cn(
            'h-1 transition-all duration-500',
            percent === 100
              ? 'bg-emerald-500'
              : percent > 50
              ? 'bg-primary-600'
              : 'bg-amber-500'
          )}
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* Task List (Desplegable) */}
      {!isCollapsed && (
        <div className="p-4 space-y-2 animate-in fade-in-50 duration-200">
          {tasks.length === 0 ? (
            <div className="text-center py-5 px-4">
              <CheckCircle2 className="w-7 h-7 text-slate-300 dark:text-gray-700 mx-auto mb-1.5" />
              <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                No hay tareas operativas asignadas a este caso
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                Las tareas generadas por el motor de IA o agregadas manualmente aparecerán aquí.
              </p>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAdding(true)}
                  className="mt-3 text-xs gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Agregar primera tarea
                </Button>
              )}
            </div>
          ) : (
            tasks.map((task) => {
            const urgency = urgencyBadgeConfig[task.urgency] || urgencyBadgeConfig.MEDIO
            return (
              <div
                key={task.id}
                className={cn(
                  'group flex items-start justify-between gap-3 p-3 rounded-lg border transition-all',
                  task.isCompleted
                    ? 'bg-slate-50/60 dark:bg-gray-800/30 border-slate-200/60 dark:border-gray-800 opacity-75'
                    : 'bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800 hover:border-primary-300 shadow-2xs'
                )}
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <button
                    type="button"
                    disabled={!canEdit || updateTasksMutation.isPending}
                    onClick={() => handleToggleTask(task.id)}
                    className={cn(
                      'mt-0.5 w-5 h-5 rounded flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500',
                      task.isCompleted
                        ? 'bg-emerald-600 text-white border border-emerald-600'
                        : 'border border-slate-300 dark:border-gray-600 hover:border-primary-500 bg-white dark:bg-gray-800'
                    )}
                  >
                    {task.isCompleted && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        'text-sm font-medium leading-snug',
                        task.isCompleted
                          ? 'line-through text-slate-400 dark:text-gray-500'
                          : 'text-slate-800 dark:text-slate-200'
                      )}
                    >
                      {task.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5 text-2xs text-slate-500 dark:text-slate-400">
                      <span
                        className={cn(
                          'inline-flex items-center px-1.5 py-0.5 rounded font-semibold border',
                          urgency.bg,
                          urgency.text,
                          urgency.border
                        )}
                      >
                        {task.urgency}
                      </span>
                      {task.assigneeRole && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-slate-300">
                          {task.assigneeRole}
                        </span>
                      )}
                      {task.isCompleted && task.completedBy && (
                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                          ✓ Resuelto por {task.completedBy}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleDeleteTask(task.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-600 rounded transition-opacity"
                    title="Eliminar tarea"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )
          })
        )}

        {/* Inline Add Task Form */}
        {isAdding && (
          <form
            onSubmit={handleAddTask}
            className="p-3.5 rounded-lg border border-primary-200 dark:border-primary-900 bg-primary-50/30 dark:bg-primary-950/20 space-y-3 mt-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-primary-900 dark:text-primary-300">
                Agregar Acción o Tarea Operativa
              </span>
              <button
                type="button"
                onClick={() => setIsAdding(false)}
                className="text-slate-400 hover:text-slate-600 text-xs"
              >
                Cancelar
              </button>
            </div>

            <Input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Ej: Solicitar soporte de pago a contabilidad antes de las 4 PM..."
              autoFocus
              className="text-sm bg-white dark:bg-gray-900"
            />

            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <div className="flex items-center gap-2">
                <select
                  value={newUrgency}
                  onChange={(e) => setNewUrgency(e.target.value as any)}
                  className="text-xs rounded-md border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-slate-700 dark:text-slate-300"
                >
                  <option value="CRITICO">Urgencia: Crítica</option>
                  <option value="ALTO">Urgencia: Alta</option>
                  <option value="MEDIO">Urgencia: Media</option>
                  <option value="BAJO">Urgencia: Baja</option>
                </select>

                <input
                  type="text"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  placeholder="Rol o Área (ej: Nómina)"
                  className="text-xs rounded-md border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-slate-700 dark:text-slate-300 w-36"
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsAdding(false)}
                  className="text-xs h-7"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!newDesc.trim()}
                  className="text-xs h-7"
                >
                  Guardar Tarea
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
      )}
    </div>
  )
}
